/**
 * LifeUp programs catalog.
 *
 * Each entry below drives one public landing page (rendered by ProgramLandingPage)
 * and the Home program-showcase grid. To launch a new program publicly, add an
 * entry to this object — no new routes or page files needed.
 *
 * Mandatory keys per program:
 *   slug, name, tagline, description, heroImage,
 *   colors { primary, secondary, accent, glow },
 *   problems[], solution { headline, bullets[] },
 *   benefits[], features[], testimonials[], pricing,
 *   cta { headline, label, url, secondaryLabel?, secondaryUrl? },
 *   seo { title, description, keywords[] }
 *
 * Optional:
 *   status: 'live' | 'coming_soon'
 *   appStoreUrl, playStoreUrl
 *   stats[]
 */

import {
  Shield, Heart, Brain, Zap, Users, Trophy, BarChart3, BookOpen,
  LifeBuoy, GraduationCap, Sparkles, MessageSquare, Bell, Lock,
  Compass, Activity, DollarSign, Target, Clock, TrendingUp,
} from 'lucide-react';

export const programs = {
  freedom: {
    slug: 'freedom',
    name: 'Freedom',
    tagline: 'Liberdade da pornografia',
    subtitle: 'Recupere foco, energia e qualidade de vida',
    description:
      'Programa estruturado de recuperação da compulsão por pornografia, com gamificação, comunidade anônima e ferramentas baseadas em neurociência.',
    heroImage: '/assets/images/app-mockup.svg',
    status: 'live',

    colors: {
      primary: '#1A95FF',
      secondary: '#7044FF',
      accent: '#10DC60',
      glow: 'rgba(26, 149, 255, 0.35)',
    },

    problems: [
      {
        icon: 'Brain',
        title: 'Compulsão e dopamina',
        description:
          'O ciclo de busca e recompensa rouba seu foco, motivação e capacidade de sentir prazer nas coisas reais.',
      },
      {
        icon: 'Heart',
        title: 'Isolamento emocional',
        description:
          'Vergonha e segredo afastam você das pessoas que importam, alimentando o ciclo de fuga.',
      },
      {
        icon: 'Activity',
        title: 'Perda de energia',
        description:
          'Sono ruim, queda de libido, ansiedade e cansaço crônico — sintomas que ninguém conecta à causa.',
      },
      {
        icon: 'Target',
        title: 'Produtividade quebrada',
        description:
          'Horas perdidas, foco fragmentado e metas adiadas — uma vida no piloto automático.',
      },
    ],

    solution: {
      headline: 'Freedom é o método que reconstrói seu cérebro, seus hábitos e sua identidade',
      description:
        'Combinamos ciência da recuperação, gamificação inteligente e comunidade anônima para quebrar o ciclo de uma vez por todas.',
      bullets: [
        'Plano diário guiado, do dia 1 ao dia 365',
        'Protocolo de emergência ativo em qualquer recaída iminente',
        'Comunidade anônima 24/7 com pares e mentores',
        'Métricas reais de progresso (dias, economia, ganhos de saúde)',
      ],
    },

    benefits: [
      { icon: 'Zap',        title: 'Mais energia',       description: 'Recupere disposição física e mental em poucas semanas.' },
      { icon: 'Brain',      title: 'Foco profundo',      description: 'Atenção e clareza mental restauradas.' },
      { icon: 'Heart',      title: 'Conexões reais',     description: 'Vínculos saudáveis e intimidade verdadeira.' },
      { icon: 'TrendingUp', title: 'Confiança',          description: 'Autoestima e disciplina que se acumulam dia após dia.' },
    ],

    features: [
      {
        icon: 'Trophy',
        title: 'Gamificação imersiva',
        description: 'Conquistas, níveis e desafios diários que tornam a jornada vívida.',
        bullets: ['Badges de progresso', 'Sistema de níveis', 'Desafios 7/30/90 dias', 'Conquistas exclusivas'],
        color: 'primary',
      },
      {
        icon: 'Users',
        title: 'Comunidade anônima',
        description: 'Grupos blindados de apoio, sem identidade exposta.',
        bullets: ['Grupos por estágio', 'Chat em tempo real', 'Mentoria por pares', 'Fóruns temáticos'],
        color: 'secondary',
      },
      {
        icon: 'BarChart3',
        title: 'Analytics de liberdade',
        description: 'Cada dia limpo vira dado — saúde, economia, ganhos.',
        bullets: ['Contador de alta precisão', 'Economia financeira', 'Indicadores de saúde', 'Relatórios semanais'],
        color: 'tertiary',
      },
      {
        icon: 'BookOpen',
        title: 'Diário emocional',
        description: 'Identifique gatilhos com diário criptografado ponta-a-ponta.',
        bullets: ['Check-in de humor', 'Mapeamento de gatilhos', 'Privacidade absoluta', 'Reflexões guiadas'],
        color: 'success',
      },
      {
        icon: 'LifeBuoy',
        title: 'Protocolo de emergência',
        description: 'Botão de pânico com técnicas anti-craving em segundos.',
        bullets: ['Pânico 24/7', 'Técnicas anti-craving', 'Respiração guiada', 'Apoio imediato'],
        color: 'danger',
      },
      {
        icon: 'GraduationCap',
        title: 'Base científica',
        description: 'Conteúdo de neurociência e psicologia da recuperação.',
        bullets: ['Artigos especializados', 'Cursos em vídeo', 'Podcasts', 'Reprogramação neural'],
        color: 'warning',
      },
    ],

    testimonials: [
      {
        name: 'Lucas, 28',
        role: '90 dias livre',
        avatar: '/assets/images/avatar-1.jpg',
        text: 'Recuperei o que tinha de mais valioso: meu foco. As primeiras semanas foram difíceis, mas o Freedom me deu estrutura.',
      },
      {
        name: 'Renato, 34',
        role: '6 meses livre',
        avatar: '/assets/images/avatar-2.jpg',
        text: 'A comunidade anônima foi o ponto de virada. Pela primeira vez não me senti sozinho na recuperação.',
      },
      {
        name: 'Felipe, 25',
        role: '1 ano livre',
        avatar: '/assets/images/avatar-3.jpg',
        text: 'O botão de emergência me salvou várias vezes. Hoje tenho uma vida que não trocaria por nada.',
      },
    ],

    stats: [
      { value: '50k+', label: 'Vidas transformadas' },
      { value: '87%',  label: 'Concluem 30 dias' },
      { value: '4.9★', label: 'Avaliação do app' },
      { value: '24/7', label: 'Suporte da comunidade' },
    ],

    pricing: {
      headline: 'Comece grátis. Evolua quando quiser.',
      description: 'Plano gratuito completo para começar. Premium libera comunidade avançada, conteúdo exclusivo e relatórios profundos.',
      plans: [
        {
          name: 'Free',
          price: 'R$ 0',
          period: 'para sempre',
          features: ['Contador e diário', 'Comunidade base', 'Conteúdo essencial', 'Protocolo de emergência'],
          cta: 'Começar grátis',
        },
        {
          name: 'Premium',
          price: 'R$ 29',
          period: 'por mês',
          highlighted: true,
          features: ['Tudo do Free', 'Comunidade premium', 'Conteúdo completo', 'Mentoria por pares', 'Relatórios avançados'],
          cta: 'Assinar Premium',
        },
      ],
    },

    cta: {
      headline: 'Sua primeira semana livre começa hoje',
      description: 'Mais de 50 mil pessoas já reescreveram sua história com o Freedom.',
      label: 'Baixar o Freedom',
      url: 'https://app.lifeup.com.br/freedom/register',
      secondaryLabel: 'Conhecer comunidade',
      secondaryUrl: '#community',
    },

    seo: {
      title: 'Freedom — Recuperação da compulsão por pornografia | LifeUp',
      description:
        'Programa estruturado para superar o vício em pornografia. Gamificação, comunidade anônima, protocolo de emergência e ciência da recuperação.',
      keywords: ['pornografia', 'recuperação', 'no-fap', 'reboot', 'compulsão sexual', 'lifeup'],
    },
  },

  balance: {
    slug: 'balance',
    name: 'Balance',
    tagline: 'Controle sobre apostas e jogos de azar',
    subtitle: 'Recupere o controle das suas finanças e decisões',
    description:
      'Programa estruturado para sair do ciclo de apostas esportivas, cassinos online e jogos de azar — com plano financeiro, comunidade e ferramentas de bloqueio.',
    heroImage: '/assets/images/app-mockup.svg',
    status: 'live',

    colors: {
      primary: '#10DC60',
      secondary: '#1A95FF',
      accent: '#F59E0B',
      glow: 'rgba(16, 220, 96, 0.35)',
    },

    problems: [
      {
        icon: 'DollarSign',
        title: 'Espiral financeira',
        description:
          'Cada aposta promete recuperar a anterior — e a dívida só cresce. O ciclo precisa parar antes que tudo desabe.',
      },
      {
        icon: 'Brain',
        title: 'Gatilho mental constante',
        description:
          'Notificações, ao vivo, push de odds: o cérebro fica preso em loop de excitação e fuga.',
      },
      {
        icon: 'Heart',
        title: 'Relações em crise',
        description:
          'Mentira sobre dinheiro corrói confiança em casa e no trabalho — e a vergonha alimenta novas apostas.',
      },
      {
        icon: 'Clock',
        title: 'Tempo sequestrado',
        description:
          'Horas e horas observando placares, odds, lives — uma vida vivida dentro de uma tela de apostas.',
      },
    ],

    solution: {
      headline: 'Balance reconstrói sua relação com dinheiro, tempo e decisões',
      description:
        'Combinamos plano financeiro estruturado, bloqueio de gatilhos, comunidade anônima e ferramentas de autocontrole para sair do ciclo de apostas.',
      bullets: [
        'Plano de recuperação financeira passo a passo',
        'Bloqueio inteligente de sites e apps de apostas',
        'Comunidade anônima de quem já saiu do ciclo',
        'Dashboard de finanças, gatilhos e progresso',
      ],
    },

    benefits: [
      { icon: 'DollarSign', title: 'Controle financeiro', description: 'Pare o sangramento e comece a reconstruir.' },
      { icon: 'Brain',      title: 'Mente em paz',        description: 'Saia do ciclo de excitação e arrependimento.' },
      { icon: 'Clock',      title: 'Tempo recuperado',    description: 'Horas livres de telas e placares.' },
      { icon: 'Heart',      title: 'Relações curadas',    description: 'Reconstrua confiança em casa e no trabalho.' },
    ],

    features: [
      {
        icon: 'Lock',
        title: 'Bloqueador inteligente',
        description: 'Bloqueia apps, sites e notificações de apostas com camada anti-burla.',
        bullets: ['Bloqueio de sites de apostas', 'Bloqueio de notificações', 'Camada anti-burla', 'Modo ferreo'],
        color: 'primary',
      },
      {
        icon: 'BarChart3',
        title: 'Plano financeiro',
        description: 'Estruture renegociação, reserva e novos hábitos com dinheiro.',
        bullets: ['Mapeamento de dívidas', 'Plano de quitação', 'Reserva de segurança', 'Educação financeira'],
        color: 'success',
      },
      {
        icon: 'Users',
        title: 'Comunidade anônima',
        description: 'Grupos com quem viveu o mesmo ciclo — sem julgamento.',
        bullets: ['Grupos por estágio', 'Chat 24/7', 'Mentoria por pares', 'Fóruns temáticos'],
        color: 'secondary',
      },
      {
        icon: 'LifeBuoy',
        title: 'Protocolo de gatilho',
        description: 'Botão de emergência com plano imediato anti-recaída.',
        bullets: ['Pânico 24/7', 'Plano de 5 minutos', 'Respiração guiada', 'Suporte imediato'],
        color: 'danger',
      },
      {
        icon: 'BookOpen',
        title: 'Diário de decisões',
        description: 'Mapeie gatilhos, padrões e contexto de cada impulso.',
        bullets: ['Check-in diário', 'Mapa de gatilhos', 'Reflexões guiadas', 'Privacidade total'],
        color: 'warning',
      },
      {
        icon: 'Trophy',
        title: 'Gamificação',
        description: 'Cada dia sem apostar é um marco — visível e celebrado.',
        bullets: ['Conquistas', 'Níveis de progresso', 'Desafios 7/30/90', 'Marcos de economia'],
        color: 'tertiary',
      },
    ],

    testimonials: [
      {
        name: 'Diego, 31',
        role: '6 meses sem apostar',
        avatar: '/assets/images/avatar-1.jpg',
        text: 'Em 3 meses parei de mentir para minha esposa sobre dinheiro. O plano financeiro do Balance é o que faltava.',
      },
      {
        name: 'Marcos, 27',
        role: '1 ano sem apostar',
        avatar: '/assets/images/avatar-2.jpg',
        text: 'O bloqueador me deu fôlego para começar. A comunidade anônima foi o que me manteve.',
      },
      {
        name: 'Tiago, 38',
        role: '90 dias sem apostar',
        avatar: '/assets/images/avatar-3.jpg',
        text: 'Pela primeira vez vejo minha conta crescendo em vez de evaporar nos finais de semana.',
      },
    ],

    stats: [
      { value: '15k+',  label: 'Em recuperação' },
      { value: 'R$ 12M', label: 'Economizados pelos usuários' },
      { value: '4.8★',   label: 'Avaliação do app' },
      { value: '24/7',   label: 'Comunidade ativa' },
    ],

    pricing: {
      headline: 'Comece sem custo. Avance quando estiver pronto.',
      description: 'Use o essencial gratuitamente. Premium libera bloqueador avançado, comunidade premium e plano financeiro guiado.',
      plans: [
        {
          name: 'Free',
          price: 'R$ 0',
          period: 'para sempre',
          features: ['Contador e diário', 'Comunidade base', 'Bloqueio básico', 'Protocolo de emergência'],
          cta: 'Começar grátis',
        },
        {
          name: 'Premium',
          price: 'R$ 29',
          period: 'por mês',
          highlighted: true,
          features: ['Tudo do Free', 'Bloqueador anti-burla', 'Plano financeiro', 'Mentoria', 'Relatórios completos'],
          cta: 'Assinar Premium',
        },
      ],
    },

    cta: {
      headline: 'O primeiro dia sem apostar começa agora',
      description: 'Você não precisa fazer sozinho. O Balance te acompanha do dia 1 em diante.',
      label: 'Baixar o Balance',
      url: 'https://app.lifeup.com.br/balance/register',
      secondaryLabel: 'Conhecer comunidade',
      secondaryUrl: '#community',
    },

    seo: {
      title: 'Balance — Recuperação de apostas e jogos de azar | LifeUp',
      description:
        'Programa para sair do ciclo de apostas esportivas, cassinos online e jogos de azar. Bloqueador, plano financeiro, comunidade e ciência.',
      keywords: ['apostas', 'jogos de azar', 'compulsão', 'cassino', 'recuperação financeira', 'lifeup'],
    },
  },
};

/**
 * Programas que ainda não lançaram, mas já aparecem no portal LifeUp
 * como "Em breve" — para validar interesse e capturar leads.
 */
export const upcomingPrograms = [
  {
    slug: 'focus',
    name: 'Focus',
    tagline: 'Reconquiste sua atenção',
    description: 'Programa contra compulsão por redes sociais e bem-estar digital.',
    colors: { primary: '#F59E0B', glow: 'rgba(245, 158, 11, 0.35)' },
    icon: 'Compass',
    status: 'coming_soon',
  },
  {
    slug: 'sober',
    name: 'Sober',
    tagline: 'Liberdade do álcool',
    description: 'Programa de apoio à sobriedade com comunidade e plano diário.',
    colors: { primary: '#7044FF', glow: 'rgba(112, 68, 255, 0.35)' },
    icon: 'Sparkles',
    status: 'coming_soon',
  },
];

/**
 * Map of lucide icon names → components, used by sections that read icon names
 * from the data file (since JSX cannot be serialized in a config).
 */
export const ICONS = {
  Shield, Heart, Brain, Zap, Users, Trophy, BarChart3, BookOpen,
  LifeBuoy, GraduationCap, Sparkles, MessageSquare, Bell, Lock,
  Compass, Activity, DollarSign, Target, Clock, TrendingUp,
};

export const getProgram = (slug) => programs[String(slug || '').toLowerCase()] || null;

export const listPrograms = () => Object.values(programs);

export default programs;
